import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CustomButton } from '../StyledComponents/SampleStyledComponents';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  generateCustomModuleScript,
  generateModuleScript,
  generateVoiceover,
  getGeneratedModuleScript,
} from '../../api/apiClient';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import ScriptLoadingModal from './ScriptLoadingModal';
import {
  ModuleEditorContext,
  setFields,
} from '../../reducers/ModuleEditorReducer';
import { ModuleScript } from '../../types';

const Container = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  justifyContent: 'center',
});

interface Props {
  isModuleScriptCreated: boolean;
  communicationStyleA: string;
  communicationStyleB: string;
  communicationStyleC: string;
}

function GenerateScriptButtons({
  isModuleScriptCreated,
  communicationStyleA,
  communicationStyleB,
  communicationStyleC,
}: Props) {
  const [
    isGetGeneratedModuleScriptEnabled,
    setIsGetGeneratedModuleScriptEnabled,
  ] = useState(isModuleScriptCreated);
  const { moduleId } = useParams();
  const queryClient = useQueryClient();
  const context = useContext(ModuleEditorContext);
  if (!context) {
    throw new Error(
      'GenerateScriptButton must be used within a ModuleEditorContext.Provider',
    );
  }
  const { dispatch } = context;

  const {
    mutate: generateModuleScriptMutation,
    isPending: isGenerateModuleScriptLoading,
  } = useMutation({
    mutationFn: (moduleId: string) =>
      generateModuleScript({
        moduleId,
        communicationStyleA,
        communicationStyleB,
        communicationStyleC,
      }),
  });

  const emptyModuleScript: ModuleScript = {
    summary: '',
    moment0: '',
    pathA: [{ learnerText: '', characterText: '', feedback: '' }],
    pathB: [{ learnerText: '', characterText: '', feedback: '' }],
    pathC: [{ learnerText: '', characterText: '', feedback: '' }],
  };

  const {
    mutate: generateCustomModuleScriptMutation,
    isPending: isGenerateCustomModuleScriptLoading,
  } = useMutation({
    mutationFn: (moduleId: string) =>
      generateCustomModuleScript({
        moduleId,
        moduleScript: emptyModuleScript,
      }),
  });

  const { mutate: generateVoiceoverMutation } = useMutation({
    mutationFn: (moduleId: string) => generateVoiceover({ moduleId }),
  });

  const {
    data: generatedModuleScriptResponse,
    isLoading: isGetGeneratedModuleScriptLoading,
  } = useQuery({
    queryKey: ['generatedModuleScript', moduleId],
    queryFn: () => getGeneratedModuleScript(moduleId ? moduleId : ''),
    refetchInterval: (query) => {
      return query.state.data?.status === 202 ? 5000 : false;
    },
    enabled: isGetGeneratedModuleScriptEnabled,
  });

  useEffect(() => {
    if (generatedModuleScriptResponse?.data) {
      dispatch(
        setFields({ moduleScript: generatedModuleScriptResponse?.data }),
      );
      queryClient.invalidateQueries({ queryKey: ['moduleDataById', moduleId] });
      handleGenerateVoiceover();
    }
  }, [generatedModuleScriptResponse]);

  const handleGenerateAIScript = async () => {
    generateModuleScriptMutation(moduleId ? moduleId : '', {
      onSuccess: () => {
        setIsGetGeneratedModuleScriptEnabled(true);
        queryClient.invalidateQueries({
          queryKey: ['generatedModuleScript', moduleId],
        });
      },
      onError: (err) => console.log(err),
    });
  };

  const handleGenerateVoiceover = () => {
    generateVoiceoverMutation(moduleId ? moduleId : '', {
      onSuccess: ({ data }) => {
        queryClient.setQueryData(['moduleVoiceOverById'], data);
        queryClient.invalidateQueries({
          queryKey: ['moduleDataById', moduleId],
        });
        console.log('onSuccess generateVoiceoverMutation');
      },
      onError: (err) => console.log(err),
    });
  };
  const handleGenerateCustomScript = () => {
    generateCustomModuleScriptMutation(moduleId ? moduleId : '', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['moduleDataById', moduleId],
        });
        console.log('onSuccess generateCustomModuleScriptMutation');
      },
      onError: (err) => console.log(err),
    });
  };

  if (isGenerateModuleScriptLoading || isGetGeneratedModuleScriptLoading) {
    return <LinearProgress />;
  }

  return (
    <Container>
      <CustomButton
        variant="contained"
        onClick={handleGenerateAIScript}
        startIcon={<AutoAwesomeIcon />}
      >
        AI generate script
      </CustomButton>
      <CustomButton variant="contained" onClick={handleGenerateCustomScript}>
        Write script yourself
        {isGenerateCustomModuleScriptLoading && (
          <CircularProgress
            size={16}
            sx={{ color: 'white', marginLeft: '10px' }}
          />
        )}
      </CustomButton>
      <ScriptLoadingModal
        scriptLoading={generatedModuleScriptResponse?.status === 202}
      />
    </Container>
  );
}

export default GenerateScriptButtons;
